import { getWorkerServiceById } from "@/const/workerService";
import { OrderDetail } from "@/types/order/Order";
import { formatISO, parseISO } from "@/utils/date";

interface Address {
  addressRegion: string | null;
  addressLocality: string | null;
  streetAddress: string | null;
}

export class JobPosting {
  constructor(private readonly orderDetail: OrderDetail) {}

  public toLDJson(): string {
    const officeAddress = JobPosting.splitAddress(
      this.orderDetail.office.address
    );

    // フォーマットについては以下を参照のこと:
    // https://developers.google.com/search/docs/appearance/structured-data/job-posting?hl=ja
    return JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      title: this.buildTitle(),
      description: this.orderDetail.description,
      datePosted: this.orderDetail.createdAt,
      hiringOrganization: {
        "@type": "Organization",
        name: this.orderDetail.office.corporationName
        // websiteが無いためsameAsは無し
        // ロゴ画像が無いためlogoは無し
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          streetAddress: officeAddress.streetAddress,
          addressLocality: officeAddress.addressLocality,
          addressRegion: officeAddress.addressRegion,
          // 郵便番号が無いためpostalCodeは無し
          addressCountry: "JP"
        }
      },
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "JPY",
        value: {
          "@type": "QuantitativeValue",
          value: this.orderDetail.hourlyWage,
          unitText: "HOUR"
        }
      },
      employmentType: "PART_TIME",
      identifier: {
        "@type": "PropertyValue",
        name: this.orderDetail.office.name,
        value: this.orderDetail.office.id
      },
      validThrough: formatISO(
        this.orderDetail.workTimeOptions
          .map(o => parseISO(o.visibleTo))
          .sort((a, b) => b - a)[0]
      )
      // `experienceRequirements` も指定できると良いかもしれない
    });
  }

  private buildTitle(): string {
    // titleというのは求人のタイトルではなくjob title (役割) のことを指す。
    //
    // > 職務の名称（求人情報のタイトルではない）。たとえば、"Software Engineer"、"Barista" のように指定します。次に例を示します。
    // > title プロパティには、求人コード、住所、日付、給与、会社名は含めないでください。
    // > 非推奨例: ブカレストでフランス語を話せる IT 技術者を募集中
    // > 推奨例: マーケティング専門家、フランス語話者
    //
    // かつては「workerServiceの内容 (介護 or 看護)」と「operationsの名前」を「、」でjoinしたものをtitleとしていたが
    // Google側がこれをワードサラダ的なスパムと看做しているのか求人票に表示されないため、
    // 現在はシンプルに「【パートタイム】 (介護 or 看護)」というタイトルにしている。
    return `【パートタイム】${getWorkerServiceById(
      this.orderDetail.workerService
    ).displayName ||
      // 本来はWORKER_SERVICESのいずれかに必ず適合するため以下には来ないはずだが、
      // 万が一のためにoperationsのうちのひとつをバックアップのために埋めておく。
      this.orderDetail.operations[0]?.name ||
      // 以下にも来ないはずだが、仮に来てしまうとundefinedが表示されてしまうため、
      // それを回避するために最終手段として空文字でfallbackする。
      ""}`;
  }

  private static splitAddress(flatAddress: string): Address {
    // NOTE:
    // orderDetail.office.addressの中に入っている住所が構造化されておらず、
    // 1つの連結された文字列として取り扱われているため、以下の正規表現に従って
    // 分割を行なっている。
    // 懸念としては、regionとlocalityのいずれか、あるいは両方が欠落し、
    // streetAddressにすべてfallbackする可能性があるが、こちらはbizに確認済。
    // ref: https://caitech.slack.com/archives/CKU3JF873/p1730981339417859?thread_ts=1728603911.601199&cid=CKU3JF873
    const addressPattern = /^(.+?[都道府県])?(.+?[市区町村郡])?(.+)?$/;

    const match = flatAddress.match(addressPattern);

    if (match) {
      return {
        addressRegion: match[1] || null,
        addressLocality: match[2] || null,
        streetAddress: match[3] || null
      };
    }
    return {
      addressRegion: null,
      addressLocality: null,
      streetAddress: null
    };
  }
}
